import type { ElementCreator } from '../element.model';
import type { FunctionCreator, FunctionParams } from './function.model';
import { AbsoluteCreator } from './absolute';
import { CosineCreator } from './cosine';
import { CubicCreator } from './cubic';
import { CubicGeneralCreator } from './cubic-general';
import { ExponentialCreator } from './exponential';
import { LinearCreator } from './linear';
import { LogarithmicCreator } from './logarithmic';
import { QuadraticCreator } from './quadratic';
import { QuadraticVertexCreator } from './quadratic-vertex';
import { SineCreator } from './sine';
import { SqrtCreator } from './sqrt';
import { TangentFnCreator } from './tangent-fn';
import { RationalCreator } from './rational';
import { RationalGeneralCreator } from './rational-general';

/**
 * Registry of all function creators.
 * Add new function types here as they are implemented.
 */
export class FunctionRegistry {
  private static readonly creators: Array<FunctionCreator<FunctionParams>> = [
    new LinearCreator(),
    new QuadraticCreator(),
    new QuadraticVertexCreator(),
    new SineCreator(),
    new CosineCreator(),
    new ExponentialCreator(),
    new LogarithmicCreator(),
    new AbsoluteCreator(),
    new CubicCreator(),
    new CubicGeneralCreator(),
    new SqrtCreator(),
    new TangentFnCreator(),
    new RationalCreator(),
    new RationalGeneralCreator(),
  ];

  /**
   * Get all registered function creators as ElementCreators.
   */
  static getAll(): ElementCreator[] {
    return this.creators;
  }

  /**
   * Get a specific function creator by type.
   */
  static get(type: string): FunctionCreator<FunctionParams> | undefined {
    return this.creators.find((c) => c.functionType === type);
  }

  /**
   * Get all registered function types.
   */
  static getTypes(): string[] {
    return this.creators.map((c) => c.functionType);
  }

  /**
   * Get the computed value keys for a function type.
   */
  static getComputedValueKeys(type: string): readonly string[] {
    return this.get(type)?.computedValueKeys ?? [];
  }
}
